import { Banner, Box, Modal, Stack, Spinner, Text } from '@stitch-fix/mode-react';
import { G } from '@mobily/ts-belt';
import React, { useCallback } from 'react';
import { useGetBoughtItemsFeedbackModalQuery } from '../../utils/hooks/getBoughtItemsFeedbackModal';
import { FeedbackForm, FeedbackFormInputValues, InputFormNameValues } from './FeedbackForm';
import { KeptItemClientFeedbackUpdateInput } from '../../types/definitions/vendor/graphql-schema.d';
import { useUpdateBoughtItemFeedbackMutation } from '../../utils/hooks/updateBoughtItemFeedback';
import { useToast } from '../../utils/contexts/toast-provider';
import { COPY } from '../../utils/copy';
export type FeedbackModalProps = {
  open: boolean;
  handleModalClose: () => void;
  skuId: string;
  orderId: string;
  returnInProgress?: boolean;
};
export type ModalHeadingProps = {
  brand: string | null | undefined;
  name: string | null | undefined;
  size: string;
};
export type MultipleChoiceQuestion = {
  __typename?: 'KeptItemFeedbackMultipleChoiceQuestion';
  questionId: InputFormNameValues;
  label: string;
  options: MultipleChoiceQuestionOption[];
};
type MultipleChoiceQuestionOption = {
  __typename?: 'KeptItemFeedbackMultipleChoiceQuestionOption';
  label: string;
  pointValue: string;
  questionId: string;
  value: boolean;
};
type FreeFormQuestion = {
  __typename?: 'KeptItemFeedbackFreeformQuestion';
  questionId: string;
  label: string;
  value?: string | null;
  secondaryLabel?: string | null;
};
export type FeedbackQuestion = MultipleChoiceQuestion | FreeFormQuestion;
const {
  feedbackUpdateSuccessMessage,
  errorToastMessage
} = COPY;

/**
 * Loading state component
 */
const loadingState = () => <Box data-testid="feedback-form-loading" style={{
  height: 700,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}}>
    <Spinner />
  </Box>;

/**
 * Renders the heading of the bought item feedback modal
 */
const ModalHeading = ({
  brand,
  name,
  size
}: ModalHeadingProps) => <Stack>
    <Text setting="eyebrow-medium">{brand}</Text>
    <Text family="regular" setting="title-medium">
      {name}
    </Text>
    <Text scale="2">Size: {size}</Text>
  </Stack>;

/**
 * Extracts & shapes the comment input question from the group of feedbackQuestions
 */
const commentQuestion = (feedbackQuestions: FeedbackQuestion[]) => {
  const inputQuestion = feedbackQuestions.find(question => question.questionId.includes('other_comments') || '');
  if (inputQuestion?.__typename === 'KeptItemFeedbackFreeformQuestion') {
    return {
      questionId: inputQuestion?.questionId,
      value: inputQuestion?.value || ''
    };
  }
  return {
    questionId: '',
    value: ''
  };
};

/**
 * Extracts our multiple choice questions from the group of feedbackQuestions
 */
const multipleChoiceQuestions = (feedbackQuestions: FeedbackQuestion[]): MultipleChoiceQuestion[] => {
  return feedbackQuestions.filter((question): question is MultipleChoiceQuestion => question.__typename === 'KeptItemFeedbackMultipleChoiceQuestion');
};

/**
 * Shapes the data for consumption by useUpdateBoughtItemFeedbackMutation
 */
const formatInputData = (inputData: FeedbackFormInputValues, feedbackQuestions: FeedbackQuestion[], questionSetId: string, skuId: string, orderId: string): KeptItemClientFeedbackUpdateInput => {
  return {
    feedback: {
      questionSetId,
      questions: Object.entries(inputData).map(questionSet => ({
        questionId: questionSet[0],
        value: questionSet[1]
      }))
    },
    feedbackContext: 'wardrobe_bought_items',
    resourceId: orderId,
    skuId
  };
};

/**
 * FeedbackModal - houses the Feedback flow of a bought item
 */
export const FeedbackModal = ({
  open,
  handleModalClose,
  skuId,
  orderId,
  returnInProgress
}: FeedbackModalProps) => {
  const {
    data,
    loading
  } = useGetBoughtItemsFeedbackModalQuery({
    variables: {
      skuId,
      orderId
    },
    skip: !open,
    fetchPolicy: 'network-only'
  });
  const {
    addToast
  } = useToast();
  const notEligibleForFeedback = data?.client?.wardrobe.boughtModule.boughtItemFeedbackModal.boughtItem.orderType === 'fix_shipment';

  /**
   * Handler for events to be triggered upon the completion of
   * useUpdateBoughtItemFeedbackMutation
   */
  const handleFeedbackUpdateCompleted = () => {
    handleModalClose();
    addToast({
      message: feedbackUpdateSuccessMessage,
      status: 'success'
    });
  };
  const [updateBoughtItemFeedback] = useUpdateBoughtItemFeedbackMutation({
    onCompleted: () => handleFeedbackUpdateCompleted(),
    onError: () => addToast({
      message: errorToastMessage,
      status: 'error'
    })
  });

  /**
   * handler for calling useUpdateBoughtItemFeedbackMutation
   */
  const handleUpdateFeedback = useCallback((inputData: KeptItemClientFeedbackUpdateInput) => {
    updateBoughtItemFeedback({
      variables: {
        input: inputData
      }
    });
  }, [updateBoughtItemFeedback]);
  if (!G.isObject(data) || !G.isObject(data?.client)) return null;
  const {
    boughtItem,
    returnHeading,
    heading,
    returnBody,
    feedbackQuestions,
    subheading
  } = data.client.wardrobe.boughtModule.boughtItemFeedbackModal;
  const {
    shopProduct,
    variant,
    eligibleForReturn
  } = boughtItem;

  /**
   * Our submit handler, passed to FeedbackForm
   */
  const onSubmit = (inputData: FeedbackFormInputValues) => {
    const formattedInputData = formatInputData(inputData, feedbackQuestions.questions as FeedbackQuestion[], feedbackQuestions.questionSetId, skuId, orderId);
    handleUpdateFeedback(formattedInputData);
  };
  const renderModalContentBasedOnOrderType = () => {
    if (notEligibleForFeedback) {
      return <Box my={1}>
          <Banner headline="Feedback saved at checkout" variant="info-light">
            You left feedback on this item during your Fix checkout. We&apos;ll
            use that feedback to help elevate your style recommendations.
          </Banner>
        </Box>;
    }
    return <>
        <Text data-testid="product-title" setting="title-small" mt={1.5} mb={0.5}>
          {heading}
        </Text>
        <Text setting="body-medium">{subheading}.</Text>
        <FeedbackForm returnHeading={returnHeading} returnBody={returnBody} eligibleForReturn={eligibleForReturn} commentQuestion={commentQuestion(feedbackQuestions.questions as FeedbackQuestion[])} multipleChoiceQuestions={multipleChoiceQuestions(feedbackQuestions.questions as FeedbackQuestion[])} onUpdate={onSubmit} orderId={orderId} returnInProgress={returnInProgress} />
      </>;
  };
  return <Modal isOpen={open} heading={<ModalHeading brand={shopProduct?.brand.name} name={shopProduct?.name} size={variant.size.name} />} onClose={() => handleModalClose()}>
      {loading ? loadingState() : renderModalContentBasedOnOrderType()}
    </Modal>;
};
export default FeedbackModal;